import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getMovieById, movies } from '@/mocks/movies';

const categoryLabels: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
};

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const movie = articleId ? getMovieById(articleId) : undefined;

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(m => m.id !== movie.id)
      .filter(m => m.genres.some(g => movie.genres.includes(g)) || m.category === movie.category)
      .slice(0, 6);
  }, [movie]);

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} ${movie.englishTitle} - 蘑菇影视高清在线观看 | 4K蓝光`;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [movie]);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
              </div>
              <h2 className="text-lg font-heading font-bold text-foreground-200 mb-2">影片未找到</h2>
              <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的影片不存在或已下架</p>
              <Link
                to="/"
                className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-foreground-50 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer inline-block"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map(name => ({
      '@type': 'Person',
      name,
    })),
    genre: movie.genres,
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
            <img
              src={movie.backdrop}
              alt={`${movie.title} - 蘑菇影视`}
              title={`${movie.title} ${categoryLabels[movie.category] || ''} 高清在线观看`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30"></div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 -mt-32 md:-mt-40 relative z-10">
            <div className="max-w-[1440px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: categoryLabels[movie.category] || movie.category, path: `/category/${movie.category}` },
                  { label: movie.title },
                ]}
              />

              <div className="mt-4 flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0 w-40 md:w-56">
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} - 蘑菇影视高清在线观看`}
                      title={`${movie.title} ${categoryLabels[movie.category] || ''} 高清在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                    {movie.title}
                  </h1>
                  <p className="text-sm text-foreground-400 mt-1">{movie.englishTitle}</p>

                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <span className="px-2.5 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded-full whitespace-nowrap">
                      {movie.quality}
                    </span>
                    <span className="flex items-center gap-1 text-accent-400 text-sm">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-xs"></i>
                      </div>
                      {movie.rating}
                    </span>
                    <span className="text-sm text-foreground-400">{movie.year}</span>
                    <span className="text-sm text-foreground-400">{movie.duration}</span>
                    <span className="text-sm text-foreground-400">{movie.region}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {movie.genres.map((genre) => (
                      <span
                        key={genre}
                        className="px-2.5 py-0.5 text-xs bg-background-100 text-foreground-300 rounded-full border border-background-200/50 whitespace-nowrap"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-xs text-foreground-500 w-12 flex-shrink-0 whitespace-nowrap">导演</span>
                      <span className="text-sm text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-xs text-foreground-500 w-12 flex-shrink-0 whitespace-nowrap">主演</span>
                      <span className="text-sm text-foreground-200">{movie.cast.join(' / ')}</span>
                    </div>
                    {movie.episodes && (
                      <div className="flex items-start gap-3">
                        <span className="text-xs text-foreground-500 w-12 flex-shrink-0 whitespace-nowrap">集数</span>
                        <span className="text-sm text-foreground-200">共{movie.episodes}集</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-foreground-50 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex items-center gap-2 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-fill text-sm"></i>
                      </div>
                      立即播放
                    </button>
                    <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 border border-background-200/50 text-foreground-300 hover:text-foreground-100 text-sm rounded-full transition-all duration-200 whitespace-nowrap flex items-center gap-2 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-heart-line text-sm"></i>
                      </div>
                      收藏
                    </button>
                  </div>
                </div>
              </div>

              {movie.synopsis && (
                <section className="mt-10">
                  <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">剧情简介</h2>
                  <article className="p-5 rounded-xl bg-background-100 border border-background-200/50">
                    <p className="text-sm text-foreground-300 leading-relaxed">{movie.synopsis}</p>
                  </article>
                </section>
              )}

              {relatedMovies.length > 0 && (
                <section className="mt-10 pb-12">
                  <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                    {relatedMovies.map((m) => (
                      <MovieCard
                        key={m.id}
                        id={m.id}
                        title={m.title}
                        poster={m.poster}
                        year={m.year}
                        rating={m.rating}
                        quality={m.quality}
                        category={m.category}
                        genres={m.genres}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}